import { gql } from '@apollo/client'

/**
 * What a storefront *offers* for one checkout rule — the calendar days it can
 * deliver on, or the payment methods it accepts.
 *
 * The third of the three things called `checkoutRules`, and the only one this
 * document touches. It reads the `CheckoutRules` collection, not
 * `configuration.checkoutRules` (the cart validation map) and not
 * `session.checkoutRules` (what the shopper already picked). Mixing the first
 * two disables a storefront's rule enforcement outright — COM-736.
 *
 * ⚠️ **`data` is a leaf and must stay one.** The schema types it `[JSON]`, so
 * `data { ... }` is a `FieldsOnCorrectType` validation error: the server
 * returns no `data` for the query at all, rather than a partial result. The
 * same mistake against `session.checkoutRules` would have broken the whole
 * boot path, which is why `getSession/types.ts` carries the long version of
 * this warning and why `tests/query.test.ts` asserts the shape against the
 * parsed AST rather than the query string.
 *
 * ⚠️ **`ruleType` is `String!` on the wire, and `sessionUid` is `ID!`.** The
 * schema names no enum for either, so a typo in `ruleType` reaches the server
 * and comes back as an error. `IGetCheckoutRuleVariables` types it as
 * `CheckoutRules` to close that on this side. Note the mutation's own
 * `sessionUid` is `String!` — the two operations disagree, and each
 * declaration matches its own field.
 *
 * **Failure, not emptiness, is how "this storefront offers nothing" arrives.**
 * `getRule` throws `ERROR.NOT_FOUND` when no rule document exists, and
 * `pushError` returns a raised `InternalError` unchanged, so the response
 * carries `extensions.code === '404'`. Apollo's default `errorPolicy` of
 * `'none'` then discards `data`. Classify on the failure; testing the payload
 * for null inspects something already thrown away.
 * @internal
 */
const getCheckoutRuleQuery = gql`
  query GetCheckoutRule(
    $storefrontName: String!
    $ruleType: String!
    $sessionUid: ID!
  ) {
    getCheckoutRule(
      storefrontName: $storefrontName
      ruleType: $ruleType
      sessionUid: $sessionUid
    ) {
      ruleType
      data
    }
  }
`

export default getCheckoutRuleQuery
